import { z } from 'zod';
import { MoneySchema } from './common.js';
// Prisma Decimal columns serialize as canonical strings; allow either string
// or non-negative number on the wire (matches ar.ts / loan.ts convention).
const DecimalLike = z.union([MoneySchema, z.coerce.number().nonnegative()]);
// Decimals that may be negative (e.g. book-tax differences capitalize negative
// principal when payment < interest in a given accounting bucket).
const SignedDecimalLike = z.union([MoneySchema, z.coerce.number()]);
// ============================================================
// Enums
// ============================================================
export const LeaseStatusSchema = z.enum(['ACTIVE', 'TERMINATED', 'EXPIRED']);
export const LeaseScheduleStatusSchema = z.enum(['PENDING', 'POSTED']);
export const LeasePaymentFrequencySchema = z.enum([
    'MONTHLY',
    'QUARTERLY',
    'YEARLY',
    'TRIENNIAL',
    'QUINQUENNIAL',
    'LUMP_SUM',
]);
export const DayCountConventionSchema = z.enum([
    'ACTUAL_ACTUAL',
    'ACTUAL_365',
    'ACTUAL_360',
    'THIRTY_360',
]);
export const LeasePaymentStatusSchema = z.enum(['PENDING', 'PAID']);
// ============================================================
// Lease — header
// ============================================================
export const LeaseSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    voucherNo: z.string().min(1),
    code: z.string().min(1).max(64),
    description: z.string().min(1),
    lessorVendorId: z.uuid().nullable().optional(),
    lessorName: z.string().min(1),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    startDate: z.coerce.date(),
    endDate: z.coerce.date(),
    months: z.number().int().positive(),
    paymentAmount: DecimalLike,
    paymentFrequency: LeasePaymentFrequencySchema,
    paymentDayOfMonth: z.number().int().min(1).max(31),
    paymentMonthOfYear: z.number().int().min(1).max(12).nullable().optional(),
    hasRentSteps: z.boolean().default(false),
    dayCountConvention: DayCountConventionSchema,
    taxAnnualRate: DecimalLike,
    paymentTermsDescription: z.string(),
    // Annual discount rate as percentage (e.g. 5.5 = 5.5%/yr).
    discountRate: DecimalLike,
    presentValue: DecimalLike,
    rouAsset: DecimalLike,
    leaseLiability: DecimalLike,
    accumulatedRouDep: DecimalLike,
    status: LeaseStatusSchema,
    journalEntryId: z.uuid().nullable().optional(),
    postedBy: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// List-row shape — adds derived display fields for table rendering.
export const LeaseListRowSchema = LeaseSchema.extend({
    lessorDisplayName: z.string(),
    monthsElapsed: z.number().int().min(0),
    rouNbv: DecimalLike,
});
// ============================================================
// LeaseSchedule — child row
// ============================================================
export const LeaseScheduleSchema = z.object({
    id: z.uuid(),
    leaseId: z.uuid(),
    period: z.string().regex(/^\d{6}$/), // YYYYMM
    periodStartDate: z.coerce.date(),
    periodEndDate: z.coerce.date(),
    daysInPeriod: z.number().int().min(0),
    openingLiability: DecimalLike,
    payment: DecimalLike,
    interestExpense: DecimalLike,
    principalReduction: SignedDecimalLike,
    closingLiability: DecimalLike,
    rouDepreciation: DecimalLike,
    bookExpense: DecimalLike,
    taxExpense: DecimalLike,
    bookTaxDiff: SignedDecimalLike,
    cumulativeBookTaxDiff: SignedDecimalLike,
    status: LeaseScheduleStatusSchema,
    journalEntryId: z.uuid().nullable().optional(),
    postedAt: z.coerce.date().nullable().optional(),
    postedBy: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// ============================================================
// LeasePayment — payment ledger row
// ============================================================
export const LeasePaymentSchema = z.object({
    id: z.uuid(),
    leaseId: z.uuid(),
    sequenceNo: z.number().int().min(1),
    dueDate: z.coerce.date(),
    amount: DecimalLike,
    status: LeasePaymentStatusSchema,
    paidDate: z.coerce.date().nullable().optional(),
    paidAmount: DecimalLike.nullable().optional(),
    journalEntryId: z.uuid().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// ============================================================
// LeaseRentStep — stepped/escalating rent within a single lease. fromMonth/
// toMonth are 1-indexed inclusive against the lease's startDate. The full
// step list must contiguously cover [1, totalMonths] (validated server-side).
// ============================================================
export const LeaseRentStepSchema = z.object({
    id: z.uuid(),
    leaseId: z.uuid(),
    sequenceNo: z.number().int().min(1),
    fromMonth: z.number().int().min(1),
    toMonth: z.number().int().min(1),
    amount: DecimalLike,
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const CreateLeaseRentStepSchema = z.object({
    fromMonth: z.coerce.number().int().min(1),
    toMonth: z.coerce.number().int().min(1),
    amount: z.coerce.number().positive(),
});
// Detail shape — lease header + full amortization schedule + payment ledger
// + rent steps.
export const LeaseWithScheduleSchema = LeaseSchema.extend({
    lessorDisplayName: z.string(),
    schedules: z.array(LeaseScheduleSchema),
    payments: z.array(LeasePaymentSchema),
    rentSteps: z.array(LeaseRentStepSchema).optional(),
});
// ============================================================
// Wire shape — POST /leases
// ============================================================
// voucherNo / presentValue / rouAsset / leaseLiability / accumulatedRouDep /
// status / journalEntryId all server-computed. The service generates the
// full LeaseSchedule (one row per calendar month) AND LeasePayment ledger
// at creation time, all PENDING.
export const CreateLeaseSchema = z
    .object({
    code: z.string().min(1).max(64),
    description: z.string().min(1),
    lessorVendorId: z.uuid().nullable().optional(),
    lessorName: z.string().min(1),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    startDate: z.iso.date(),
    endDate: z.iso.date(),
    months: z.coerce.number().int().min(1).max(360),
    paymentAmount: z.coerce.number().positive(),
    paymentFrequency: LeasePaymentFrequencySchema.default('MONTHLY'),
    paymentDayOfMonth: z.coerce.number().int().min(1).max(31).default(1),
    paymentMonthOfYear: z.coerce.number().int().min(1).max(12).optional(),
    paymentTermsDescription: z.string().default('End-of-month'),
    // Annual discount rate as percentage (e.g. 5.5). 0 is allowed for
    // related-party intra-group leases.
    discountRate: z.coerce.number().nonnegative().max(100),
    dayCountConvention: DayCountConventionSchema.default('ACTUAL_ACTUAL'),
    taxAnnualRate: z.coerce.number().nonnegative().max(100).default(20),
    // Optional stepped rent. When non-empty the builder uses these amounts
    // instead of paymentAmount for each cycle (looked up by month-from-start).
    // Server validates contiguous coverage of [1, months].
    rentSteps: z.array(CreateLeaseRentStepSchema).optional(),
})
    .refine((v) => !['YEARLY', 'TRIENNIAL', 'QUINQUENNIAL'].includes(v.paymentFrequency) ||
    v.paymentMonthOfYear !== undefined, {
    message: 'paymentMonthOfYear is required for YEARLY / TRIENNIAL / QUINQUENNIAL frequencies',
    path: ['paymentMonthOfYear'],
});
// ============================================================
// Wire shape — POST /leases/amortize
// ============================================================
// Posts all PENDING LeaseSchedule rows for the given period across every
// active lease in the company. Returns the list of (leaseId, journalEntryId)
// pairs that were posted.
export const AmortizeLeasesSchema = z.object({
    period: z.string().regex(/^\d{6}$/, 'period must be YYYYMM'),
    // Optional override of bank account code for the cash leg. Defaults to
    // 1112 (operating bank) when omitted.
    bankAccountCode: z.string().min(1).max(32).optional(),
});
// ============================================================
// Wire shape — POST /leases/:id/terminate  &  /leases/:id/terminate/preview
// ============================================================
// Early termination (TFRS 16 derecognition). terminationDate must fall within
// the lease term. penalty is the cash paid to the lessor (0 if none). The
// service auto-catches-up un-amortized interest + ROU dep to the date, then
// derecognizes ROU + liability with the difference (± penalty) to P&L.
export const TerminateLeaseSchema = z.object({
    terminationDate: z.iso.date(),
    penalty: z.coerce.number().nonnegative().default(0),
    reason: z.string().max(500).optional(),
    bankAccountCode: z.string().min(1).max(32).optional(),
});
export const PreviewTerminationSchema = z.object({
    terminationDate: z.iso.date(),
    penalty: z.coerce.number().nonnegative().default(0),
});
const TerminationGainLossKindSchema = z.enum(['GAIN', 'LOSS', 'NEUTRAL']);
export const TerminationPreviewSchema = z.object({
    leaseId: z.uuid(),
    voucherNo: z.string(),
    terminationDate: z.string(),
    catchUp: z.object({
        rowCount: z.number().int().min(0),
        interest: DecimalLike,
        depreciation: DecimalLike,
        payment: DecimalLike,
        rows: z.array(z.object({
            period: z.string(),
            kind: z.enum(['FULL', 'PARTIAL']),
            interest: DecimalLike,
            depreciation: DecimalLike,
            payment: DecimalLike,
        })),
    }),
    remainingLiability: DecimalLike,
    accumulatedDepreciation: DecimalLike,
    rouCost: DecimalLike,
    rouNbv: DecimalLike,
    penalty: DecimalLike,
    gainLoss: SignedDecimalLike,
    gainLossKind: TerminationGainLossKindSchema,
    derecognitionLines: z.array(z.object({
        accountCode: z.string(),
        debit: DecimalLike,
        credit: DecimalLike,
        description: z.string(),
    })),
});
export const TerminationResultSchema = z.object({
    leaseId: z.uuid(),
    voucherNo: z.string(),
    status: z.literal('TERMINATED'),
    terminationDate: z.string(),
    catchUpInterest: DecimalLike,
    catchUpDepreciation: DecimalLike,
    catchUpPayment: DecimalLike,
    remainingLiability: DecimalLike,
    rouNbv: DecimalLike,
    penalty: DecimalLike,
    gainLoss: SignedDecimalLike,
    gainLossKind: TerminationGainLossKindSchema,
    catchUpJournalEntryIds: z.array(z.string()),
    terminationJournalEntryId: z.string(),
});
// ============================================================
// Wire shape — POST /leases/:id/modify  &  /leases/:id/modify/preview
// ============================================================
// Mid-term modification (TFRS 16 remeasurement). The new rent applies from
// effectiveDate through lease end as month-ranges 1-indexed from the effective
// month (single step = flat new rent). Liability is remeasured at the revised
// discount rate; the change adjusts the ROU asset (no P&L except the ROU-floor
// gain edge case).
export const ModifyRentStepSchema = z.object({
    fromMonth: z.coerce.number().int().min(1),
    toMonth: z.coerce.number().int().min(1),
    amount: z.coerce.number().positive(),
});
export const ModifyLeaseSchema = z.object({
    effectiveDate: z.iso.date(),
    revisedDiscountRate: z.coerce.number().nonnegative().max(100),
    newRentSteps: z.array(ModifyRentStepSchema).min(1),
    reason: z.string().max(500).optional(),
    bankAccountCode: z.string().min(1).max(32).optional(),
});
export const PreviewModificationSchema = z.object({
    effectiveDate: z.iso.date(),
    revisedDiscountRate: z.coerce.number().nonnegative().max(100),
    newRentSteps: z.array(ModifyRentStepSchema).min(1),
});
const ModificationAdjustmentKindSchema = z.enum(['DECREASE', 'INCREASE', 'NEUTRAL']);
export const ModificationPreviewSchema = z.object({
    leaseId: z.uuid(),
    voucherNo: z.string(),
    effectiveDate: z.string(),
    revisedDiscountRate: DecimalLike,
    catchUp: z.object({
        rowCount: z.number().int().min(0),
        interest: DecimalLike,
        depreciation: DecimalLike,
        payment: DecimalLike,
    }),
    oldLiability: DecimalLike,
    newLiability: DecimalLike,
    liabilityAdjustment: SignedDecimalLike,
    oldRouNbv: DecimalLike,
    newRouCarrying: DecimalLike,
    rouAdjustment: SignedDecimalLike,
    plGain: DecimalLike,
    adjustmentKind: ModificationAdjustmentKindSchema,
    newSchedule: z.object({
        rowCount: z.number().int().min(0),
        paymentCount: z.number().int().min(0),
        totalPayments: DecimalLike,
        firstPayment: DecimalLike,
    }),
    remeasureLines: z.array(z.object({
        accountCode: z.string(),
        debit: DecimalLike,
        credit: DecimalLike,
        description: z.string(),
    })),
});
export const ModificationResultSchema = z.object({
    leaseId: z.uuid(),
    voucherNo: z.string(),
    sequenceNo: z.number().int().min(1),
    effectiveDate: z.string(),
    oldLiability: DecimalLike,
    newLiability: DecimalLike,
    liabilityAdjustment: SignedDecimalLike,
    rouAdjustment: SignedDecimalLike,
    plGain: DecimalLike,
    catchUpJournalEntryIds: z.array(z.string()),
    remeasureJournalEntryId: z.string().nullable(),
});
export const AmortizeLeasesResultSchema = z.object({
    period: z.string(),
    postedCount: z.number().int().min(0),
    totalInterest: DecimalLike,
    totalPrincipal: SignedDecimalLike,
    totalPayment: DecimalLike,
    totalDepreciation: DecimalLike,
    entries: z.array(z.object({
        leaseId: z.uuid(),
        leaseVoucherNo: z.string(),
        scheduleId: z.uuid(),
        journalEntryId: z.string(),
        paymentJournalEntryId: z.uuid().nullable(),
    })),
});
//# sourceMappingURL=lease.js.map